import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { useHistory } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import API from "../../api";
import { capture } from "../../instrument";
import { INPUT_CLASS_NAME } from "../../utils";

const AddLaboratory = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [laboratoryCode, setLaboratoryCode] = useState("");
  const [hprimCode, setHprimCode] = useState("");
  const [samplerCode, setSamplerCode] = useState("CLR");
  const [homePageMessage, setHomePageMessage] = useState("");
  const [isVisible, setIsVisible] = useState(true);
  const [coordinates, setCoordinates] = useState([0, 0]);

  const createMutation = useMutation({
    mutationFn: (body) => API.post("/admin/laboratory", body),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      toast.success("Laboratoire ajouté avec succès");
      queryClient.invalidateQueries({ queryKey: ["laboratories"] });
      history.push("/laboratory");
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const addLaboratory = () => {
    if (!name || !address) return toast.error("Veuillez remplir tous les champs obligatoires");
    createMutation.mutate({
      name,
      address,
      phone,
      laboratoryCode,
      hprimCode,
      samplerCode,
      homePageMessage,
      isVisible,
      location: { type: "Point", coordinates },
    });
  };

  return (
    <div className="flex h-[calc(100vh-60px)] flex-col bg-gray-50">
      <div className="flex items-start justify-between border-b border-gray-200 bg-white px-8 pb-5 pt-6">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-gray-900">Ajouter un laboratoire</h1>
        </div>
        <div className="flex shrink-0 items-center gap-1.5 pl-4">
          <button
            type="button"
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
            onClick={() => history.push("/laboratory")}
          >
            Annuler
          </button>
          <button
            type="button"
            className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
            onClick={addLaboratory}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? "Chargement..." : "Ajouter"}
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-4xl px-8 py-6">
            <h3 className="mb-4 text-lg font-medium text-gray-900">Général</h3>

            <div className="divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white">
              <div className="flex items-center gap-4 px-5 py-3.5">
                <label className="w-64 shrink-0 text-[13px] font-medium text-gray-600">Nom*</label>
                <input className={INPUT_CLASS_NAME} placeholder="Nom du laboratoire" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="flex items-center gap-4 px-5 py-3.5">
                <label className="w-64 shrink-0 text-[13px] font-medium text-gray-600">Adresse*</label>
                <input className={INPUT_CLASS_NAME} placeholder="Adresse" value={address} onChange={(e) => setAddress(e.target.value)} />
              </div>
              <div className="flex items-center gap-4 px-5 py-3.5">
                <label className="w-64 shrink-0 text-[13px] font-medium text-gray-600">Téléphone</label>
                <input className={INPUT_CLASS_NAME} placeholder="Téléphone" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
              <div className="flex items-center gap-4 px-5 py-3.5">
                <label className="w-64 shrink-0 text-[13px] font-medium text-gray-600">Code HRPIM</label>
                <input className={INPUT_CLASS_NAME} placeholder="Code HRPIM" value={hprimCode} onChange={(e) => setHprimCode(e.target.value)} />
              </div>
              <div className="flex items-center gap-4 px-5 py-3.5">
                <label className="w-64 shrink-0 text-[13px] font-medium text-gray-600">Code laboratoire</label>
                <input className={INPUT_CLASS_NAME} placeholder="Code laboratoire" value={laboratoryCode} onChange={(e) => setLaboratoryCode(e.target.value)} />
              </div>
              <div className="flex items-center gap-4 px-5 py-3.5">
                <label className="w-64 shrink-0 text-[13px] font-medium text-gray-600">Code de préleveur</label>
                <input className={INPUT_CLASS_NAME} placeholder="Code de préleveur" value={samplerCode} onChange={(e) => setSamplerCode(e.target.value)} />
              </div>
              <div className="flex items-center gap-4 px-5 py-3.5">
                <label className="w-64 shrink-0 text-[13px] font-medium text-gray-600">Latitude</label>
                <input
                  type="number"
                  className={INPUT_CLASS_NAME}
                  placeholder="Latitude"
                  value={coordinates[1]}
                  onChange={(e) => setCoordinates([coordinates[0], parseFloat(e.target.value) || 0])}
                />
              </div>
              <div className="flex items-center gap-4 px-5 py-3.5">
                <label className="w-64 shrink-0 text-[13px] font-medium text-gray-600">Longitude</label>
                <input
                  type="number"
                  className={INPUT_CLASS_NAME}
                  placeholder="Longitude"
                  value={coordinates[0]}
                  onChange={(e) => setCoordinates([parseFloat(e.target.value) || 0, coordinates[1]])}
                />
              </div>
              <div className="flex items-start gap-4 px-5 py-3.5">
                <label className="w-64 shrink-0 pt-2 text-[13px] font-medium text-gray-600">Message page d'accueil</label>
                <textarea
                  className={`${INPUT_CLASS_NAME} min-h-[100px] resize-y`}
                  placeholder="Texte affiché sur la page d'accueil de l'app pour ce laboratoire (optionnel)"
                  value={homePageMessage}
                  maxLength={4000}
                  onChange={(e) => setHomePageMessage(e.target.value)}
                />
              </div>
              <div className="flex items-center justify-between gap-4 px-5 py-3.5">
                <div className="w-64 shrink-0">
                  <span className="text-[13px] font-medium text-gray-600">Visible sur l'app patient</span>
                  <span className="block text-xs text-gray-500">Affiché dans le sélecteur de laboratoire</span>
                </div>
                <button
                  type="button"
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${isVisible ? "bg-primary-500" : "bg-gray-300"}`}
                  onClick={() => setIsVisible(!isVisible)}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isVisible ? "translate-x-5" : "translate-x-0"}`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddLaboratory;
